import { Col, Form, Input, Row, notification } from 'antd';
import React, { useCallback, useState } from 'react';

import Modal, { useNoticeModal } from '@fmbt/ui/Modal';
import Text, { TextStack } from '@fmbt/ui/Text';
import dataOperationClient from 'clients/dataOperation';
import DefaultButton from 'components/atoms/DefaultButton';
import DefaultForm from 'components/composites/DefaultForm';
import DefaultFormItem from 'components/composites/DefaultFormItem';

import DeviceBasedSamplePointSelect from './DeviceBasedSamplePointSelect';

const JIRA_TICKET_REGEX = /^(FMBT|MFB)-\d+$/;

type TransferFormValues = {
  jiraTicketId: string;
  sourceSamplePointId: string;
  targetSamplePointId: string;
};

const TRANSFER_CONFIRM_FIELDS: {
  label: string;
  getValue: (values: TransferFormValues) => string;
}[] = [
    { label: 'Jira Ticket:', getValue: (values) => values.jiraTicketId },
    { label: 'From Sample Point ID:', getValue: (values) => values.sourceSamplePointId },
    { label: 'To Sample Point ID:', getValue: (values) => values.targetSamplePointId }
  ];

function TransferTabContent(): JSX.Element {
  const [form] = Form.useForm<TransferFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [transferToConfirm, setTransferToConfirm] = useState<TransferFormValues | null>(null);
  const { noticeModal, showNotice } = useNoticeModal();

  const handleConfirmTransfer = useCallback(async () => {
    if (!transferToConfirm) {
      return;
    }
    setIsSubmitting(true);
    const { data, error } = await dataOperationClient.createDataTransfer({
      jiraTicketId: transferToConfirm.jiraTicketId,
      sourceSamplePointSid: transferToConfirm.sourceSamplePointId,
      targetSamplePointSid: transferToConfirm.targetSamplePointId
    });
    setIsSubmitting(false);
    setTransferToConfirm(null);

    if (error) {
      showNotice(
        'Transfer Failed',
        error.message || 'An error occurred while creating the data transfer operation'
      );
    } else {
      notification.success({
        message: 'Data Transfer Initiated',
        description: `Operation ${data.operationUuid} has been created and will be processed shortly`,
        duration: 5
      });
      form.resetFields();
    }
  }, [form, showNotice, transferToConfirm]);

  const handleTransfer = (values: TransferFormValues) => {
    if (values.sourceSamplePointId === values.targetSamplePointId) {
      showNotice('Validation Error', 'Source and target sample points cannot be the same');
      return;
    }
    setTransferToConfirm(values);
  };

  return (
    <DefaultForm
      form={form}
      layout="vertical"
      onFinish={handleTransfer}
    >
      <Row>
        <Col span={12}>
          <DefaultFormItem
            label="Jira Ticket ID"
            name="jiraTicketId"
            normalize={(value) => value?.toUpperCase()}
            rules={[
              { required: true, message: 'Jira Ticket ID is required' },
              {
                pattern: JIRA_TICKET_REGEX,
                message: 'Must be in format FMBT-xxxx or MFB-xxxx (e.g. FMBT-1234, MFB-567)'
              }
            ]}
          >
            <Input placeholder="e.g. FMBT-1234 or MFB-567" />
          </DefaultFormItem>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
          <DefaultFormItem
            label="Source Device"
            name="sourceSamplePointId"
            rules={[{ required: true, message: 'Please select a source sample point' }]}
          >
            <DeviceBasedSamplePointSelect />
          </DefaultFormItem>
        </Col>

        <Col span={12}>
          <DefaultFormItem
            label="Target Device"
            name="targetSamplePointId"
            dependencies={['sourceSamplePointId']}
            rules={[
              { required: true, message: 'Please select a target sample point' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('sourceSamplePointId') !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Target sample point must be different from source sample point'));
                }
              })
            ]}
          >
            <DeviceBasedSamplePointSelect />
          </DefaultFormItem>
        </Col>
      </Row>

      <Row>
        <Col span={24} style={{ textAlign: 'right' }}>
          <DefaultFormItem>
            <DefaultButton type="primary" htmlType="submit" loading={isSubmitting}>
              Transfer
            </DefaultButton>
          </DefaultFormItem>
        </Col>
      </Row>

      <Modal
        confirmText="Transfer"
        isConfirmButtonLoading={isSubmitting}
        isOpen={!!transferToConfirm}
        onConfirm={handleConfirmTransfer}
        onDismiss={() => setTransferToConfirm(null)}
        size={320}
        title="Confirm Transfer"
      >
        {transferToConfirm ? (
          <TextStack v="body">
            <Text v="body">Are you sure you want to transfer data?</Text>
            <ul style={{ textAlign: 'left', paddingLeft: 16, marginBottom: 0 }}>
              {TRANSFER_CONFIRM_FIELDS.map(({ label, getValue }) => (
                <li key={label}>
                  <Text v="body">
                    <Text v="body-emphasis" inline>{label}</Text> {getValue(transferToConfirm)}
                  </Text>
                </li>
              ))}
            </ul>
          </TextStack>
        ) : undefined}
      </Modal>

      {noticeModal}
    </DefaultForm>
  );
}

export default TransferTabContent;
